import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Banner from './Banner';
import SearchForm from './SearchForm';
import Results from './Results';
import { API_BASE_URL } from '../config/api';
import { trackPageVisit, trackAction } from '../utils/tracking';
import '../App.css';

const SearchPage = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [familyMembers, setFamilyMembers] = useState({});
  const [loadingFamily, setLoadingFamily] = useState({});

  // Track page visit
  useEffect(() => {
    trackPageVisit('/');
  }, []);

  const handleSearch = async (searchData) => {
    // Track search with detailed information
    const searchDetails = {
      searchType: searchData.searchType,
      query: searchData.firstName || searchData.middleName || searchData.surname || searchData.epic || 'empty',
      firstName: searchData.firstName || null,
      middleName: searchData.middleName || null,
      surname: searchData.surname || null,
      epic: searchData.epic || null,
      timestamp: new Date().toISOString()
    };
    trackAction('/', 'search', searchDetails);
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      
      if (searchData.searchType === 'epic') {
        if (searchData.epic) params.append('epic', searchData.epic);
      } else {
        if (searchData.firstName) params.append('firstName', searchData.firstName);
        if (searchData.middleName) params.append('middleName', searchData.middleName);
        if (searchData.surname) params.append('surname', searchData.surname);
      }

      const response = await fetch(`${API_BASE_URL}/search?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setResults(data.results || []);
      } else {
        setError(data.error || 'Search failed');
        setResults([]);
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      setResults([]);
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFamilySearch = async (familyId) => {
    if (!familyId) return;
    
    // Track family search action
    trackAction('/', 'family_search', { familyId });
    
    setLoadingFamily(prev => ({ ...prev, [familyId]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/search/family/${familyId}`);
      const data = await response.json();

      if (data.success) {
        setFamilyMembers(prev => ({
          ...prev,
          [familyId]: data.results || []
        }));
      } else {
        setFamilyMembers(prev => ({
          ...prev,
          [familyId]: []
        }));
      }
    } catch (err) {
      console.error('Family search error:', err);
      setFamilyMembers(prev => ({
        ...prev,
        [familyId]: []
      }));
    } finally {
      setLoadingFamily(prev => ({ ...prev, [familyId]: false }));
    }
  };

  const siteUrl = window.location.origin;
  const ogImage = `${siteUrl}/banner1.jpg`;

  return (
    <>
      <Helmet>
        <title>पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६ - राजेंद्र (बाबु) वागसकर</title>
        <meta name="description" content="पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६ - मतदार शोध | प्रभाग क्र. १४ : कोरेगांव पार्क–घोरपडी–मुंढवा" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content="पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६ - राजेंद्र (बाबु) वागसकर" />
        <meta property="og:description" content="प्रभाग क्र. १४ : कोरेगांव पार्क–घोरपडी–मुंढवा | महाराष्ट्र नवनिर्माण सेनेचे अधिकृत उमेदवार राजेंद्र (बाबु) वागसकर" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content="पुणे महानगरपालिका सार्वत्रिक निवडणूक २०२६ - राजेंद्र (बाबु) वागसकर" />
        <meta name="twitter:description" content="प्रभाग क्र. १४ : कोरेगांव पार्क–घोरपडी–मुंढवा | महाराष्ट्र नवनिर्माण सेनेचे अधिकृत उमेदवार राजेंद्र (बाबु) वागसकर" />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      
      <div className="App">
        <Banner />
      <div className="search-banner">
        <div className="container">
          <h1 className="search-banner-title">मतदार यादीत नाव शोधा</h1>
        </div>
      </div>
      <div className="container">
        <div className="app-content">
          <SearchForm onSearch={handleSearch} loading={loading} />
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <Results 
            results={results} 
            loading={loading} 
            hasSearched={hasSearched}
            onFamilySearch={handleFamilySearch}
            familyMembers={familyMembers}
            loadingFamily={loadingFamily}
          />
        </div>
      </div>
    </div>
    </>
  );
};

export default SearchPage;
